import { Injectable, NgZone } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';

// Services
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '@src/app/shared/services/spartan-notification.service';

/**
 * Interceptor that handles HTTP errors and displays notifications.
 */
@Injectable({ providedIn: 'root' })
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private _message: NotificationService,
    private _router: Router,
    private _authService: AuthService,
    private _ngZone: NgZone,
  ) {}

  /**
   * Intercepts the HTTP request and handles any errors that occur.
   * @param request - The HTTP request.
   * @param next - The next HTTP handler in the chain.
   * @returns An observable of the HTTP event.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // HTTP responses can resolve outside Angular's zone: authenticated
        // requests are chained off Firebase token callbacks (`getIdToken`),
        // which run outside the zone. Without re-entering it here, the error
        // toast never renders and callers' spinners keep spinning. Run the
        // side effects (toast/navigate/logout) inside the zone.
        return this._ngZone.run(() => {
          let errorMsg = '';
          if (error.error instanceof ErrorEvent) {
            errorMsg = `Error: ${error.error.message}`;
          } else {
            // NestJS validation errors arrive as `message: string[]`; join them
            // so the toast shows a readable message instead of "[object Object]".
            const backendMessage = Array.isArray(error.error?.message)
              ? error.error.message.join(', ')
              : error.error?.message;

            if (backendMessage) {
              errorMsg = backendMessage;
            } else {
              errorMsg = `Error Code: ${error.status}, Message: ${error.message}`;
            }

            // If the error is a 400 and there is no message, redirect to not-found
            if (error.status === 400 && !error.error?.message) {
              this._router.navigate(['/not-found']);
            }

            if (error.status === 401) {
              this._authService.logout();
            }
            if (
              error.status === 403 &&
              error.error?.code === 'UPGRADE_REQUIRED'
            ) {
              return EMPTY;
            }
          }
          this._message.error(errorMsg);
          console.log(errorMsg);
          return throwError(() => new Error(errorMsg));
        });
      }),
    );
  }
}
